// src/auth/authErrorCopy.js
//
// Wording for every refusal on the sign-in surfaces (login, password recovery, set-password, the invitation
// accept page, Profilo) — the same `(t, err)` shape as tenantAdmin/userActionError.js and
// context/site/siteErrorCopy.js, over a buildApiError Error (`code`, `status`, `requestId`, hoisted extras).
//
// ⛔ Never `err.message`: buildApiError always fills it, with the server's English when there is any. Every
// sentence here is ours; an unmapped code is PRINTED (a support handle) through the caller's own sentence, and
// a 5xx carrying `request_id` quotes that reference instead.
//
// Shared fallbacks, in order: no response → network; 5xx with a reference → the reference; 5xx without one (a
// proxy page, whose body is not JSON) → "unreachable"; anything else → the caller's `unmappedKey`, which takes
// `{{code}}`.

/** The 5xx-with-reference sentence, or null when `err` is not one. */
export function serverReferenceCopy(t, err) {
  if (err && err.status >= 500 && err.requestId) return t('errors:auth.server', { ref: err.requestId });
  return null;
}

export function authFallbackCopy(t, err, unmappedKey) {
  if (!err || err.code === 'NETWORK' || !err.status) return t('errors:auth.network');
  const reference = serverReferenceCopy(t, err);
  if (reference) return reference;
  if (err.status >= 500) return t('errors:auth.unavailable');
  return t(unmappedKey, { code: err.code || err.status });
}

// POST /auth/login. ⛔ AUTH_USER_NOT_FOUND is what a DEACTIVATED account answers (also right after a successful
// password reset, which never touches is_active): "contact an administrator", never "wrong password".
// ⛔ RATE_LIMITED says nothing about whether the address exists — never word it as "account locked".
// A 422 is the server's address check (the browser's type=email accepts `a@b`, the backend does not).
export function loginErrorCopy(t, err) {
  switch (err?.code) {
    case 'AUTH_INVALID_CREDENTIALS': return t('errors:auth.invalidCredentials');
    case 'AUTH_USER_NOT_FOUND': return t('errors:auth.userNotFound');
    case 'RATE_LIMITED':
      return Number.isFinite(err.retry_after_seconds)
        ? t('errors:auth.rateLimited', { seconds: err.retry_after_seconds })
        : t('errors:auth.rateLimitedGeneric');
    default:
      if (err?.status === 422) return t('errors:auth.invalidEmail');
      return authFallbackCopy(t, err, 'errors:auth.genericWithCode');
  }
}
